import {
  icons as lucideIcons,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import type {
  IconContent,
  ScriptElement,
} from '../../schema';

/**
 * A few shared-registry ids that aren't 1:1 lucide glyph names — map them to the
 * closest lucide PascalCase component. Extend as new registry ids show up.
 */
const ICON_ALIASES: Record<string, string> = {
  thinking: 'Brain',
  idea: 'Lightbulb',
  tip: 'Lightbulb',
  warning: 'TriangleAlert',
  mistake: 'TriangleAlert',
  question: 'HelpCircle',
  note: 'StickyNote',
  important: 'Star',
};

/**
 * Icon element (INTEGRATION §3.2 / BLUEPRINT §13). `content.id` is a stable id
 * from the shared icon registry — usually also a canonical lucide glyph name
 * (kebab-case). lucide-react keys components in PascalCase, so we convert; a few
 * registry-only ids are aliased above. An unknown id degrades to a help-circle
 * glyph rather than throwing.
 */
function resolveLucideIcon(id: string | undefined): LucideIcon {
  if (!id) return HelpCircle;
  const map = lucideIcons as unknown as Record<string, LucideIcon>;
  if (map[id]) return map[id];
  const aliased = ICON_ALIASES[id.toLowerCase()];
  if (aliased && map[aliased]) return map[aliased];
  const pascal = id
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
  return map[pascal] ?? HelpCircle;
}

export function IconElement({
  element,
  fill,
}: {
  element: ScriptElement;
  fill?: boolean;
}) {
  const content = element.content as IconContent | undefined;
  const Icon = resolveLucideIcon(content?.id);
  // Kept identical to the admin's IconElement so an un-coloured icon looks the
  // same in the editor and the classroom (these previously diverged).
  const color = (element.style?.color as string | undefined) ?? '#1f2937';
  // Honor the authored size; in `fill` mode size to the (scaled) box height.
  const size = content?.size ?? (fill ? '100%' : 56);

  return (
    <div
      className="flex flex-col items-center justify-center gap-2"
      style={fill ? { width: '100%', height: '100%' } : undefined}
    >
      <Icon
        size={size as number}
        strokeWidth={content?.strokeWidth ?? 2}
        color={color}
        style={
          typeof size === 'string' ? { width: size, height: size } : undefined
        }
      />
      {content?.caption && (
        <span style={{ fontSize: 16, color }}>{content.caption}</span>
      )}
    </div>
  );
}
